import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, CheckCircle2, ChevronLeft, ChevronRight, Gift, Mail } from 'lucide-react';
import { SIGNUP_BONUS_AMOUNT } from '../../lib/types';
import Xed21Logo from '../brand/Xed21Logo';

const CONTACT_EMAIL = 'contact@xed21.com';

export default function HeroSection() {
  const [activeCard, setActiveCard] = useState(0);

  const totalCards = 2;

  const prev = useCallback(() => setActiveCard(i => (i - 1 + totalCards) % totalCards), []);
  const next = useCallback(() => setActiveCard(i => (i + 1) % totalCards), []);

  useEffect(() => {
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-teal-50/60 via-white to-white" />
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgb(15 118 110 / 0.06) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      <header className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Xed21Logo size="lg" />
            <nav className="hidden md:flex items-center gap-5">
              <a
                href="#pricing"
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                Pricing
              </a>
              <a
                href="#for-schools"
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                For Schools
              </a>
              <Link
                to="/blog"
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                Blog
              </Link>
              <Link
                to="/contact"
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                Contact
              </Link>
              <Link
                to="/auth"
                className="px-5 py-2.5 bg-teal-700 text-white rounded-xl text-sm font-medium hover:bg-teal-800 transition-all shadow-sm hover:shadow-md"
              >
                Sign In
              </Link>
            </nav>
            <Link
              to="/auth"
              className="md:hidden px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium"
            >
              Sign In
            </Link>
          </div>
        </div>
      </header>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-20 sm:pb-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-100/80 text-teal-700 rounded-full text-sm font-medium mb-8">
              <Sparkles className="w-4 h-4" />
              NCERT and CBSE assessment generator
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.1] tracking-tight">
              Generate NCERT-Aligned Exam Questions
              <br />
              <span className="bg-gradient-to-r from-teal-600 to-emerald-500 bg-clip-text text-transparent">
                in Seconds from &#8377;5
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-500 mt-6 leading-relaxed max-w-lg">
              Xed21 creates Bloom's Taxonomy-aligned questions across 6 formats
              for Indian classrooms. Pay only for what you generate, with
              &#8377;{SIGNUP_BONUS_AMOUNT} free in your wallet to start.
            </p>

            <div className="flex flex-wrap gap-4 mt-10">
              <Link
                to="/auth"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-teal-700 text-white rounded-xl text-sm font-semibold hover:bg-teal-800 transition-all shadow-lg shadow-teal-700/20 hover:shadow-xl hover:shadow-teal-700/30 hover:-translate-y-0.5"
              >
                <Gift className="w-4 h-4" />
                Claim &#8377;{SIGNUP_BONUS_AMOUNT} Free & Generate
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-slate-300 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-all"
              >
                See How It Works
              </a>
            </div>

            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-800"
            >
              <Mail className="h-4 w-4" />
              Contact us: {CONTACT_EMAIL}
            </a>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10">
              {[
                'NCERT/CBSE ready',
                '6 question types',
                "Bloom's levels built in",
                'No subscription',
              ].map((text) => (
                <span
                  key={text}
                  className="flex items-center gap-1.5 text-sm text-slate-500"
                >
                  <CheckCircle2 className="w-4 h-4 text-teal-500" />
                  {text}
                </span>
              ))}
            </div>
          </div>

          <div className="relative hidden lg:block">
            <div className="absolute -top-16 -right-16 w-72 h-72 bg-teal-200/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-amber-200/20 rounded-full blur-3xl" />

            <div className="relative">
              {/* Carousel viewport */}
              <div className="overflow-hidden rounded-2xl">
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${activeCard * 100}%)` }}
                >
                  {/* Card 1 */}
                  <div className="w-full flex-shrink-0 px-1">
                    <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/80 p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-7 h-7 bg-teal-700 text-white rounded-lg flex items-center justify-center text-xs font-bold">1</span>
                        <span className="text-xs bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full font-medium">hard</span>
                        <span className="text-xs bg-sky-50 text-sky-700 px-2 py-0.5 rounded-full font-medium">Analyzing</span>
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium ml-auto">MCQ</span>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed mb-3">
                        A particle moves in a plane such that its position vector is given by <strong>r(t) = (2t) î + (4t²) ĵ</strong>, where t is in seconds and r in meters.
                      </p>
                      <div className="mb-3 rounded-xl overflow-hidden border border-slate-200 bg-white">
                        <img
                          src="/Gemini_Generated_Image_n5lonen5lonen5lo.svg"
                          alt="Diagram showing particle motion with position vector components"
                          width={2816}
                          height={1536}
                          loading="eager"
                          decoding="async"
                          className="w-full h-auto"
                        />
                      </div>
                      <p className="text-sm text-slate-800 font-medium leading-relaxed">
                        Which statement correctly explains about the motion?
                      </p>
                      <div className="mt-4 space-y-2">
                        <div className="flex items-start gap-2.5 px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg">
                          <span className="w-5 h-5 bg-slate-200 text-slate-600 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">A</span>
                          <span className="text-sm text-slate-600">The acceleration varies with time, and both components of velocity change uniformly.</span>
                        </div>
                        <div className="flex items-start gap-2.5 px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg">
                          <span className="w-5 h-5 bg-slate-200 text-slate-600 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">B</span>
                          <span className="text-sm text-slate-600">The acceleration is zero, and the particle moves with constant velocity along a straight line.</span>
                        </div>
                        <div className="flex items-start gap-2.5 px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg">
                          <span className="w-5 h-5 bg-slate-200 text-slate-600 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">C</span>
                          <span className="text-sm text-slate-600">The acceleration is constant and directed along the x-axis, while the y-component of velocity is constant.</span>
                        </div>
                        <div className="flex items-start gap-2.5 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <span className="w-5 h-5 bg-emerald-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">D</span>
                          <span className="text-sm text-emerald-800 font-medium">The acceleration is constant and directed along the y-axis, while the x-component of velocity is constant.</span>
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 ml-auto flex-shrink-0 mt-0.5" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2 */}
                  <div className="w-full flex-shrink-0 px-1">
                    <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/80 p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-7 h-7 bg-teal-700 text-white rounded-lg flex items-center justify-center text-xs font-bold">2</span>
                        <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">medium</span>
                        <span className="text-xs bg-sky-50 text-sky-700 px-2 py-0.5 rounded-full font-medium">Analyzing</span>
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium ml-auto">MCQ</span>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed mb-3">
                        During a health check-up, the school nurse measures the height of all students in Grade 7. She notices that some students have grown taller than others over the past year. The nurse explains that growth spurts during adolescence are controlled by hormones.
                      </p>
                      <div className="mb-3 rounded-xl overflow-hidden border border-slate-200 bg-white">
                        <img
                          src="/Gemini_Generated_Image_avxxz7avxxz7avxx.svg"
                          alt="Chart showing student height growth over time"
                          width={2816}
                          height={1536}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-auto"
                        />
                      </div>
                      <p className="text-sm text-slate-800 font-medium leading-relaxed">
                        Which student likely experienced the most rapid growth spurt during adolescence?
                      </p>
                      <div className="mt-4 space-y-2">
                        <div className="flex items-start gap-2.5 px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg">
                          <span className="w-5 h-5 bg-slate-200 text-slate-600 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">A</span>
                          <span className="text-sm text-slate-600">Student A</span>
                        </div>
                        <div className="flex items-start gap-2.5 px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg">
                          <span className="w-5 h-5 bg-slate-200 text-slate-600 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">B</span>
                          <span className="text-sm text-slate-600">Student B</span>
                        </div>
                        <div className="flex items-start gap-2.5 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <span className="w-5 h-5 bg-emerald-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">C</span>
                          <span className="text-sm text-emerald-800 font-medium">Student C</span>
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 ml-auto flex-shrink-0 mt-0.5" />
                        </div>
                        <div className="flex items-start gap-2.5 px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg">
                          <span className="w-5 h-5 bg-slate-200 text-slate-600 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">D</span>
                          <span className="text-sm text-slate-600">Student D</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between mt-4 px-1">
                <button
                  type="button"
                  aria-label="Show previous sample question"
                  onClick={prev}
                  className="w-8 h-8 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-500 hover:text-teal-600 hover:border-teal-300 transition-all"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-2">
                  {Array.from({ length: totalCards }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      aria-label={`Show sample question ${i + 1}`}
                      aria-pressed={i === activeCard}
                      onClick={() => setActiveCard(i)}
                      className={`w-6 h-2 rounded-full transition-colors duration-300 ${
                        i === activeCard
                          ? 'bg-teal-700'
                          : 'bg-slate-300 hover:bg-slate-400'
                      }`}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  aria-label="Show next sample question"
                  onClick={next}
                  className="w-8 h-8 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-500 hover:text-teal-600 hover:border-teal-300 transition-all"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
